import { MessageCircle, X, Smile, Paperclip } from 'lucide-react'
import PostEditor from '@/components/editor/PostEditor'
import ReactionEmojiBar from './ReactionEmojiBar'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface PostPreviewProps {
  body: string
  onBodyChange: (value: string) => void
  postFormat: 'small' | 'large'
  replyType: 'none' | 'reactions' | 'text'
  reactionEmojis: string[]
  maxReactionEmojis: number
  onReactionEmojisChange: (emojis: string[]) => void
  agent: Agent | undefined
  onImageUpload?: (file: File) => Promise<string>
}

function AgentAvatar({ agent, size = 'sm' }: { agent: Agent | undefined; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'
  if (agent?.avatar_url) {
    return <img src={agent.avatar_url} alt={agent.name} className={`${sizeClass} rounded-full object-cover`} />
  }
  const initials = agent?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  return (
    <div className={`${sizeClass} rounded-full bg-blue-600 text-white flex items-center justify-center font-medium`}>
      {initials}
    </div>
  )
}

function ReplySection({
  replyType,
  reactionEmojis,
  maxReactionEmojis,
  onReactionEmojisChange,
}: {
  replyType: 'none' | 'reactions' | 'text'
  reactionEmojis: string[]
  maxReactionEmojis: number
  onReactionEmojisChange: (emojis: string[]) => void
}) {
  if (replyType === 'none') return null

  if (replyType === 'reactions') {
    return (
      <div className="border-t border-gray-100 px-4 py-2.5">
        <ReactionEmojiBar emojis={reactionEmojis} maxEmojis={maxReactionEmojis} onChange={onReactionEmojisChange} />
      </div>
    )
  }

  // text
  return (
    <div className="border-t border-gray-100 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center bg-gray-50 rounded-full px-3 py-1.5">
          <span className="text-xs text-gray-400 flex-1">Escreva uma resposta...</span>
          <div className="flex items-center gap-1.5 ml-2">
            <Smile className="w-3.5 h-3.5 text-gray-400" />
            <Paperclip className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  )
}

function SmallPostCard({ body, onBodyChange, replyType, reactionEmojis, maxReactionEmojis, onReactionEmojisChange, agent, onImageUpload }: Omit<PostPreviewProps, 'postFormat'>) {
  return (
    <div className="w-[320px] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-2.5">
        <AgentAvatar agent={agent} size="sm" />
        <span className="text-sm font-medium text-gray-900">{agent?.name || 'Agente'}</span>
      </div>

      {/* Body - Editor */}
      <div className="px-4 pb-2 text-sm text-gray-700">
        <PostEditor
          value={body}
          onChange={onBodyChange}
          placeholder="Escreva o conteúdo do post..."
          minHeight="60px"
          onImageUpload={onImageUpload}
        />
      </div>

      {/* Reply Section */}
      <ReplySection
        replyType={replyType}
        reactionEmojis={reactionEmojis}
        maxReactionEmojis={maxReactionEmojis}
        onReactionEmojisChange={onReactionEmojisChange}
      />
    </div>
  )
}

function LargePostModal({ body, onBodyChange, replyType, reactionEmojis, maxReactionEmojis, onReactionEmojisChange, agent, onImageUpload }: Omit<PostPreviewProps, 'postFormat'>) {
  return (
    <div className="w-[90%] max-w-[480px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <AgentAvatar agent={agent} size="md" />
            <span className="text-sm font-medium text-gray-900">{agent?.name || 'Agente'}</span>
          </div>
          <button type="button" className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body - Editor */}
        <div className="px-5 py-4 text-sm text-gray-700">
          <PostEditor
            value={body}
            onChange={onBodyChange}
            placeholder="Escreva o conteúdo do post..."
            minHeight="100px"
            onImageUpload={onImageUpload}
          />
        </div>

        {/* Reply Section */}
        <ReplySection
          replyType={replyType}
          reactionEmojis={reactionEmojis}
          maxReactionEmojis={maxReactionEmojis}
          onReactionEmojisChange={onReactionEmojisChange}
        />
      </div>
  )
}

export default function PostPreview({
  body,
  onBodyChange,
  postFormat,
  replyType,
  reactionEmojis,
  maxReactionEmojis,
  onReactionEmojisChange,
  agent,
  onImageUpload,
}: PostPreviewProps) {
  const sharedProps = { body, onBodyChange, replyType, reactionEmojis, maxReactionEmojis, onReactionEmojisChange, agent, onImageUpload }

  return (
    <div className="flex flex-col h-full">
      {/* Browser Chrome */}
      <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 flex items-center gap-3 rounded-t-lg">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-400 border border-gray-200">
          seusite.com.br
        </div>
      </div>

      {/* Website area */}
      <div className="flex-1 bg-gray-50 relative rounded-b-lg" style={{ minHeight: '400px' }}>
        {/* Simulated page content */}
        <div className="p-6 space-y-3 opacity-30 pointer-events-none select-none absolute inset-0">
          <div className="h-4 bg-gray-300 rounded w-3/4" />
          <div className="h-4 bg-gray-300 rounded w-1/2" />
          <div className="h-4 bg-gray-300 rounded w-5/6" />
          <div className="h-32 bg-gray-200 rounded mt-4" />
          <div className="h-4 bg-gray-300 rounded w-2/3" />
          <div className="h-4 bg-gray-300 rounded w-4/5" />
        </div>

        {/* Post Card or Modal */}
        {postFormat === 'small' ? (
          <div className="relative flex flex-col items-end gap-3 p-4 min-h-[200px] justify-end">
            <SmallPostCard {...sharedProps} />
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        ) : (
          <div className="relative flex justify-center py-6 bg-black/20 rounded-b-lg">
            <LargePostModal {...sharedProps} />
          </div>
        )}
      </div>
    </div>
  )
}
